import { debounce } from '@mui/material/utils';
import { TagType } from '@strength/domain';
import { getTMessageForEnum } from '@strength/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import { Gallery } from '../../../components/gallery/gallery';
import { FilterOption } from '../../../components/table/data-table-context';
import { useViewer } from '../../../context/viewer';
import { useFilteredGalleryPagination } from '../../../hooks/useFilteredGalleryPagination';
import { useToast } from '../../../hooks/useToast';
import { TagDrawer, TagForm } from './TagDrawer';
import { Tag } from './types';
import { libraryTagTypes, resolveTagTypeFilter } from './utils';

const columnHelper = createColumnHelper<Tag>();

export function TagsPage() {
  const { t } = useTranslation();
  const { hasPermissions } = useViewer();
  const enableCreate = hasPermissions('library.tags', 'create');
  const { toast } = useToast();
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<TagType[]>([]);

  const {
    refetch,
    data,
    pageSize,
    setPageSize,
    page,
    setPage,
    viewMode,
    setViewMode,
    isLoading,
  } = useFilteredGalleryPagination({
    queryFn: trpc.tag.searchTags.useQuery,
    queryInput: {
      searchTerm: debouncedSearchTerm,
      types: resolveTagTypeFilter(typeFilter),
    },
  });

  const createTag = trpc.tag.createTag.useMutation();
  const editTag = trpc.tag.editTag.useMutation();
  const deleteTag = trpc.tag.deleteTag.useMutation();

  const onSaveTag = useCallback(
    (form: TagForm, tagId?: string) => {
      if (tagId) {
        editTag.mutate(
          { id: tagId, name: form.name, type: form.type },
          {
            onSuccess: () => {
              toast({ title: t('tagUpdated'), severity: 'success' });
              refetch();
            },
            onError: () =>
              toast({ title: t('somethingWentWrong'), severity: 'error' }),
          },
        );
      } else {
        createTag.mutate(
          { name: form.name, type: form.type },
          {
            onSuccess: () => {
              toast({ title: t('tagCreated'), severity: 'success' });
              refetch();
            },
            onError: () =>
              toast({ title: t('somethingWentWrong'), severity: 'error' }),
          },
        );
      }
    },
    [createTag, editTag, t, refetch, toast],
  );

  const onDeleteTag = useCallback(
    (tagId: string) => {
      deleteTag.mutate(
        { id: tagId },
        {
          onSuccess: () => {
            toast({ title: t('tagDeleted'), severity: 'success' });
            refetch();
          },
          onError: () =>
            toast({ title: t('somethingWentWrong'), severity: 'error' }),
        },
      );
    },
    [deleteTag, t, refetch, toast],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => t('name'),
        cell: ({ row }) => row.original.name,
      }),
      columnHelper.accessor('type', {
        header: () => t('type'),
        cell: ({ row }) => t(...getTMessageForEnum(row.original.type)),
      }),
    ],
    [t],
  );

  const filters = useMemo(
    () => [
      {
        id: 'type',
        type: 'select' as const,
        title: t('type'),
        multiselect: true,
        options: libraryTagTypes.map<FilterOption<TagType>>((type) => ({
          id: type,
          label: t(...getTMessageForEnum(type)),
          value: type,
        })),
      },
    ],
    [t],
  );

  // Debounce the search term that goes to the query
  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    [],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSetSearchTerm(value);
      setPage(0);
    },
    [debouncedSetSearchTerm, setPage],
  );

  const handleFilterChange = useCallback(
    (filterValues: Record<string, TagType[]>) => {
      const types = filterValues['type'];
      if (types !== undefined) {
        setTypeFilter(types);
        setPage(0);
      }
    },
    [setPage],
  );

  const handleResetFilters = useCallback(() => {
    setSearchInput('');
    setDebouncedSearchTerm('');
    debouncedSetSearchTerm.clear();
    setTypeFilter([]);
    setPage(0);
  }, [debouncedSetSearchTerm, setPage]);

  return (
    <Gallery<Tag>
      title={t('tags')}
      createButtonText={t('createTag')}
      createDisabled={!enableCreate}
      createDisabledReason={t('permissionDisabled.create', {
        item: t('tags'),
      })}
      data={data}
      columns={columns}
      filters={filters}
      viewMode={viewMode}
      isLoading={isLoading}
      setViewMode={setViewMode}
      page={page}
      pageSize={pageSize}
      setPage={setPage}
      setPageSize={setPageSize}
      pageSizeOptions={[25, 50, 100]}
      searchValue={searchInput}
      onSearchChange={handleSearchChange}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      disableGridView={true}
      renderEditSheet={(selected, setSelected) => (
        <TagDrawer
          tag={selected}
          open={!!selected}
          onClose={() => setSelected(null)}
          onSubmit={onSaveTag}
          onDelete={onDeleteTag}
        />
      )}
      renderCreateSheet={
        enableCreate
          ? (isOpen, setIsOpen) => (
              <TagDrawer
                open={isOpen}
                onClose={() => setIsOpen(false)}
                onSubmit={onSaveTag}
                onDelete={onDeleteTag}
              />
            )
          : undefined
      }
    />
  );
}
